import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { checkDeleteFlowElement } from "../../../components/TaskSidebar/sidebar.helper";
import { useCancelEdit } from "../../../components/Workflow/FlowElements/flowElementscommonHook";
import { discardCancel, discardFlowElement, edit, updateCancel } from "../../../components/Workflow/workflowHelper";
import { icon, number, tooltip } from "../../../config";
import { isTrueBit } from "../../../utils";
import { GridContext } from "../../../utils/kendo";
import { Tooltip } from "@progress/kendo-react-tooltip";

const editField = "inEdit";

/**
 * Parent Component: FlowElementDetails
 * It provides edit, update, add, delete and remove functionality for a flow element
 * @author Himanshu Negi
 */
export const FlowElementCommandCell = (props) => {
  const [currentDeletedItem, setCurentDeletedItem] = useState(null);
  const { task, flowElements } = useSelector((state) => state.taskSidebar);
  const { handleCancelEdit } = useCancelEdit(props);

  const { handleFlowElementEdit, handleNewFlowElement, handleDeleteFlowElement, setPreviewAdvanced } = props;
  const { flowElementData, setFlowElementData, taskElement } = useContext(GridContext);

  /** used to delete next element of workflow
   if similar elements co exist after one element is deleted
   @author {Prachi Jain}
   */
  useEffect(() => {
    if (currentDeletedItem) handleDeleteFlowElement(currentDeletedItem);
  }, [currentDeletedItem]);

  /**discards flow element (edit mode)*/
  const handleDiscardCancel = () => {
    if (props.isRuleBasedFlow) {
      if (props.onExitEditMode) {
        props.onExitEditMode();
      }
      return;
    }
    const shouldShowAdvanced = Boolean(props.dataItem?.isApproval) || Boolean(props.dataItem?.isFlowLocked) || Boolean(props.dynamicFlow);
    if (!shouldShowAdvanced && setPreviewAdvanced) {
      setPreviewAdvanced(false);
    }
    if (isNewItem && setPreviewAdvanced) {
      setPreviewAdvanced(false);
    }
    return isNewItem ? discardFlowElement(flowElementData, setFlowElementData) : handleCancelEdit();
  };

  /**updates flow element (edit mode)*/
  const handleUpdateCancel = () => {
    // Handle rule-based flow differently
    if (props.isRuleBasedFlow) {
      // For rule-based flow, just call the save handler
      return isNewItem
        ? handleNewFlowElement({
            ...props.dataItem,
            selectedActiveMember: props.selectedActiveMember,
            dynamicFlow: props.dynamicFlow,
            profileType: props.profileType,
          })
        : handleFlowElementEdit({
            ...props.dataItem,
            selectedActiveMember: props.selectedActiveMember,
            dynamicFlow: props.dynamicFlow,
            profileType: props.profileType,
          });
    }

    // Original logic for grid-based flow
    const shouldShowAdvanced = Boolean(props.dataItem?.isApproval) || Boolean(props.dataItem?.isFlowLocked) || Boolean(props.dynamicFlow);
    if (!shouldShowAdvanced && setPreviewAdvanced) {
      setPreviewAdvanced(false);
    }
    if (isNewItem && setPreviewAdvanced) {
      setPreviewAdvanced(false);
    }
    return isNewItem
      ? handleNewFlowElement({
          ...props.dataItem,
          selectedActiveMember: props.selectedActiveMember,
          dynamicFlow: props.dynamicFlow,
          profileType: props.profileType,
        })
      : handleFlowElementEdit({
          ...props.dataItem,
          selectedActiveMember: props.selectedActiveMember,
          dynamicFlow: props.dynamicFlow,
          profileType: props.profileType,
        });
  };

  /**removes flow element on deletion*/
  const handleExistingDiscardCancel = async (dataItem) => {
    // Handle rule-based flow deletion
    if (props.isRuleBasedFlow) {
      if (props.onDeleteNode) {
        props.onDeleteNode(dataItem.nodeId);
      }
      return;
    }

    // Original deletion logic for regular workflows
    const requiredElements = taskElement ? flowElements : flowElementData;
    const deletionIndex = requiredElements?.findIndex((flow) => flow.id === dataItem?.id);
    const isValidDeletion = checkDeleteFlowElement(requiredElements, deletionIndex - number.ONE, deletionIndex + number.ONE);
    if (isValidDeletion) {
      const nextElement = requiredElements[deletionIndex + number.ONE];
      setCurentDeletedItem(nextElement);
    }
    await handleDeleteFlowElement(dataItem);
  };

  /**updates flow element*/
  const handleExistingUpdateCancel = () => {
    if (props.isRuleBasedFlow) {
      if (props.onEnterEditMode) {
        props.onEnterEditMode();
      }
      return;
    }
    return edit(props.dataItem, flowElementData, setFlowElementData);
  };

  const isNewItem = props.dataItem?.id === undefined;
  return (
    <React.Fragment>
      <span className={!props.dataItem?.[editField] ? "workflow-grid-action" : ""}>
        {props.dataItem?.[editField] ? (
          <span className='d-flex align-items-center'>
            <span className='square-icon square-icon-small flow-action-icon' id='discard-new-flow-element' onClick={handleDiscardCancel}>
              {discardCancel(isNewItem)}
            </span>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              <span className='mr-1 square-icon square-icon-small flow-action-icon' onClick={handleUpdateCancel} id='update-new-flow-element'>
                {updateCancel(isNewItem)}
              </span>
            </Tooltip>
          </span>
        ) : (
          !!(!task?.taskId || (!props.dataItem?.isCompleted && !task.isFollowed && (!(task?.IsTaskComplete || task?.IsTaskClosed) || isTrueBit(task, "InApproval")))) && (
            <span className='d-flex align-items-center'>
              <span
                className='cursor-pointer flow-action-icon'
                id='flow-element-command-cell-delete-span'
                onClick={() => handleExistingDiscardCancel(props.dataItem)}
                title={tooltip.DELETE}>
                {icon.DELETE_ICON}
              </span>
              <span
                className='ml-1 cursor-pointer flow-action-icon'
                id='flow-element-command-cell-edit-span'
                onClick={handleExistingUpdateCancel}
                title={tooltip.EDIT}>
                {icon.EDIT_ICON}
              </span>
            </span>
          )
        )}
      </span>
    </React.Fragment>
  );
};
